import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { MoreHorizontal } from 'lucide-react'
import type { TeamMember } from '@/types/team-members'
import {
  ORG_ROLE_LABELS,
  ORG_ROLE_DESCRIPTIONS,
} from '@/types/team-members'
import { formatDistanceToNow } from 'date-fns'

interface TeamMembersTableProps {
  members: TeamMember[]
  isOwner: boolean
  currentUserId?: string
  onEditRole: (member: TeamMember) => void
  onRemove: (member: TeamMember) => void
  isLoading?: boolean
}

export function TeamMembersTable({
  members,
  isOwner,
  currentUserId,
  onEditRole,
  onRemove,
  isLoading = false,
}: TeamMembersTableProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-24">
        <p className="text-sm text-muted-foreground">Loading members...</p>
      </div>
    )
  }

  if (members.length === 0) {
    return (
      <div className="flex justify-center items-center h-24">
        <p className="text-sm text-muted-foreground">No team members yet</p>
      </div>
    )
  }

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'owner':
        return 'default'
      case 'admin':
        return 'secondary'
      case 'manager':
        return 'outline'
      default:
        return 'outline'
    }
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Joined</TableHead>
            {isOwner && <TableHead className="text-right">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => (
            <TableRow key={member.user_id}>
              <TableCell className="font-medium">
                {member.display_name || 'Unnamed'}
                {member.is_primary_owner && (
                  <Badge variant="default" className="ml-2 text-xs">
                    Primary Owner
                  </Badge>
                )}
              </TableCell>
              <TableCell>{member.email}</TableCell>
              <TableCell>
                <Badge variant={getRoleBadgeVariant(member.role)}>
                  {ORG_ROLE_LABELS[member.role]}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {member.created_at
                  ? formatDistanceToNow(new Date(member.created_at), {
                      addSuffix: true,
                    })
                  : 'N/A'}
              </TableCell>
              {isOwner && (
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!member.is_primary_owner &&
                        currentUserId !== member.user_id && (
                          <>
                            <DropdownMenuItem
                              onClick={() => onEditRole(member)}
                            >
                              Change Role
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => onRemove(member)}
                              className="text-destructive"
                            >
                              Remove Member
                            </DropdownMenuItem>
                          </>
                        )}
                      {member.is_primary_owner && (
                        <DropdownMenuItem disabled className="text-xs">
                          Owner cannot be modified
                        </DropdownMenuItem>
                      )}
                      {currentUserId === member.user_id && (
                        <DropdownMenuItem disabled className="text-xs">
                          Cannot modify yourself
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="px-6 py-3 border-t bg-muted/50 text-xs text-muted-foreground">
        <p>
          <strong>Role Permissions:</strong> {Object.entries(ORG_ROLE_LABELS)
            .slice(0, 2)
            .map(([_, label]) => `${label}, `)
            .join('')}
          etc.
        </p>
        {Object.entries(ORG_ROLE_DESCRIPTIONS)
          .slice(0, 1)
          .map(([role, desc]) => (
            <p key={role}>
              <strong>{ORG_ROLE_LABELS[role as any]}:</strong> {desc}
            </p>
          ))}
      </div>
    </div>
  )
}
